import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import SEO from "../components/SEO";
import { Reveal, Eyebrow } from "../components/Reveal";
import EnquiryForm from "../components/EnquiryForm";
import LocationsSection from "../components/LocationsSection";

const DEPARTMENTS = [
  { v: "wholesale_jewellery", l: "Wholesale Jewellery", d: "Jewellers, retailers, trade accounts" },
  { v: "retail_appointment", l: "Retail Jewellery", d: "Showroom visits & family purchases" },
  { v: "property", l: "Property Purchase", d: "Plots, villas, townships" },
  { v: "site_visit", l: "Site Visit", d: "Visit a project location" },
  { v: "landowner", l: "Landowner Proposal", d: "Joint development & land sale" },
  { v: "partnership", l: "Business Partnership", d: "Institutional & strategic" },
  { v: "vendor", l: "Vendor Enquiry", d: "Suppliers & service providers" },
  { v: "careers", l: "Careers", d: "Work with RK Group" },
  { v: "media", l: "Media", d: "Press & media requests" },
  { v: "general", l: "General Enquiry", d: "Anything else" },
];

const EXTRA_FIELDS = {
  property: [
    { name: "location_interest", label: "Location of interest", type: "select",
      options: ["Sardhana", "Lawar", "Ramraj", "Putthi", "Khardoni", "Mawana", "Kharkhadi", "Kharkhauda", "Modinagar", "A new or upcoming location"] },
    { name: "property_type", label: "Property type", type: "select", options: ["Plot", "Villa", "Developed Land", "Township Unit"] },
    { name: "budget", label: "Budget range", placeholder: "e.g. ₹20–40 lakh" },
  ],
  careers: [{ name: "role_interest", label: "Area of interest", placeholder: "e.g. sales, site operations, accounts" }],
  vendor: [{ name: "business", label: "Business / service offered", required: true }],
  media: [{ name: "outlet", label: "Publication / outlet", required: true }],
  partnership: [{ name: "organisation", label: "Organisation", required: true }],
};

export default function Contact() {
  const [params, setParams] = useSearchParams();
  const [config, setConfig] = useState({});
  const type = DEPARTMENTS.some((d) => d.v === params.get("type")) ? params.get("type") : "general";
  const active = DEPARTMENTS.find((d) => d.v === type);

  useEffect(() => {
    api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="contact-page" className="pt-28 sm:pt-36">
      <SEO title="Contact" description="Speak to the right team at RK Group — wholesale jewellery, retail jewellery, property, site visits, landowner proposals, partnerships, vendors, careers and media." />
      <header className="max-w-[1400px] mx-auto px-5 sm:px-8 pb-12">
        <Eyebrow>Contact</Eyebrow>
        <h1 className="font-serif font-light text-4xl sm:text-6xl tracking-tight text-[#FBF9F5] mt-4">
          Speak to the <span className="italic text-[#C5A059]">Right Team</span>
        </h1>
      </header>

      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#94A3B8] font-semibold mb-4">Choose a department</p>
          <div className="space-y-2" data-testid="contact-department-list">
            {DEPARTMENTS.map((d) => (
              <button key={d.v} data-testid={`contact-dept-${d.v}`}
                onClick={() => setParams({ type: d.v }, { replace: true })}
                className={`w-full text-left px-5 py-4 border transition-colors duration-300 ${
                  type === d.v
                    ? "border-[#C5A059] bg-[#0B1320]"
                    : "border-white/10 hover:border-[#C5A059]/40"
                }`}>
                <span className={`font-serif text-lg ${type === d.v ? "text-[#C5A059]" : "text-[#FBF9F5]"}`}>{d.l}</span>
                <span className="block text-[11px] text-[#94A3B8] mt-1">{d.d}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-8">
          <Reveal key={type} className="border border-white/10 bg-[#0B1320] p-8 sm:p-12">
            <EnquiryForm
              type={type}
              title={active.l}
              intro={`Your enquiry is routed directly to the ${active.l.toLowerCase()} desk.`}
              fields={EXTRA_FIELDS[type] || []}
            />
          </Reveal>
        </div>
      </section>

      <LocationsSection />
      {(config.whatsapp_jewellery || config.whatsapp_realestate) && (
        <div className="bg-[#0B1320] pb-20">
          <div className="max-w-[1400px] mx-auto px-5 sm:px-8 flex flex-wrap gap-4">
            {config.whatsapp_jewellery && (
              <a href={`https://wa.me/${config.whatsapp_jewellery}`} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-jewellery"
                className="text-[11px] uppercase tracking-[0.2em] font-semibold border border-[#C5A059]/60 text-[#C5A059] px-6 py-3.5 hover:bg-[#C5A059] hover:text-[#060A12] transition-colors duration-300">
                WhatsApp — Jewellery Desk
              </a>
            )}
            {config.whatsapp_realestate && (
              <a href={`https://wa.me/${config.whatsapp_realestate}`} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-realestate"
                className="text-[11px] uppercase tracking-[0.2em] font-semibold border border-[#C5A059]/60 text-[#C5A059] px-6 py-3.5 hover:bg-[#C5A059] hover:text-[#060A12] transition-colors duration-300">
                WhatsApp — Real Estate Desk
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
